import React, { useMemo, useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { Box, CircularProgress } from "@mui/material";
import PullToRefresh from "react-simple-pull-to-refresh";

import NotificationBGImage from "@/assets/notification_bg.png";

import FullScreenDialog from "@/components/FullScreenDialog";
import Dialog from "@/components/Dialog";
import Header from "@/components/Header";
import GenericNotificationCard from "@/components/GenericNotificationCard";
import NotificationsEmptyState from "./NotificationsEmptyState";
import GetImages from "@/components/GetImages";
import PermissionWrapper from "@/components/PermissionWrapper";

import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";

import {
  IInAppNotifiationsPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  config,
  fetchInAppNotification,
  resetUnreadInAppNotification,
  setInAppNotification,
} from "@/store/slices/config";

import useInfiniteScroll from "@/utils/hooks/useInfiniteScroll";
import {
  getFirebaseStatusApi,
  getInAppNotificationApi,
} from "@/utils/apis/config";
import { INotifyEnum, notify } from "@/utils/toaster";
import { supportNumber } from "@/utils/Config";

interface IProps {
  isOpen: boolean;
  closeHandler: () => void;
}

const Notification: React.FC<IProps> = ({ isOpen, closeHandler }) => {
  const {
    inAppNotification,
    isAppNotificationLoading,
    unreadNotificationIds,
    isAllNotificationRead,
  } = useAppSelector(config) || {};
  const dispatch = useAppDispatch();

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();
  const intl = useIntl();

  const { handleFetchNext, currentPage, handleResetPage } =
    useInfiniteScroll<any>({
      fetchFn: fetchInAppNotification,
      fetchNextFn: fetchInAppNotification,
    });

  const notificationCloseHandler = () => {
    closeHandler();
    dispatch(resetUnreadInAppNotification());
  };

  const changeNotificationStatus = async () => {
    try {
      const response = await getFirebaseStatusApi({
        notificationIds: unreadNotificationIds,
      });
    } catch (error) {
      notify(INotifyEnum.ERROR, "Something went wrong");
    }
  };

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const buffer = 100;

      const bottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - buffer;

      if (bottom && !isAppNotificationLoading) {
        handleFetchNext({ page: currentPage + 1 });
      }
    },
    [handleFetchNext, currentPage, isAppNotificationLoading]
  );

  const handleNotificationRefresh = async () => {
    handleResetPage();
    try {
      const response = await getInAppNotificationApi({ page: 1 });
      if ("result" in response) {
        dispatch(setInAppNotification(response?.result?.notificationData));
      } else {
        notify(INotifyEnum.ERROR, response?.message);
      }
    } catch (error) {
      notify(INotifyEnum.ERROR, "Something went wrong");
    }
  };

  const notificationInnerContent = useMemo(() => {
    return (
      <PermissionWrapper
        moduleKey={IModalKeyEnums.NOTIFICATION}
        featureKey={IInAppNotifiationsPermissionEnums.IN_APP_NOTIFICATIONS}
      >
        <Box>
          <Grid
            item
            sx={{ position: "sticky", top: 0, zIndex: 100, width: "100%" }}
          >
            <Header
              leftBackArrowShow={isMobile || isTablet}
              heading={intl.formatMessage({
                id: "notifications",
                defaultMessage: "Notifications",
              })}
              subHeading={intl.formatMessage({
                id: "stay_updated_with_all_offers",
                defaultMessage: "Stay updated with all offers",
              })}
              onGoBack={notificationCloseHandler}
              secondOption={
                <>
                  {!(isMobile || isTablet) ? (
                    <IconButton onClick={notificationCloseHandler}>
                      <CloseIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={() =>
                        (window.location.href = `tel:${supportNumber}`)
                      }
                    >
                      <GetImages
                        name="CallPatientIcon"
                        width={"30"}
                        height={"30"}
                      />
                    </IconButton>
                  )}
                </>
              }
            />
          </Grid>
          <PullToRefresh onRefresh={handleNotificationRefresh}>
            <Grid
              item
              sx={{
                backgroundImage: `url(${NotificationBGImage})`,
                backgroundSize: "contain",
                minWidth: { xs: "auto", md: "46.4rem" },
                height: "70rem",
                overflowY: "auto",
              }}
              onScroll={handleScroll}
            >
              {inAppNotification?.length > 0 ? (
                <Grid item m={1}>
                  {inAppNotification?.map((notification) => (
                    <GenericNotificationCard
                      key={notification.id}
                      {...notification}
                    />
                  ))}
                  {isAppNotificationLoading && (
                    <Stack direction={"row"} justifyContent={"center"}>
                      <CircularProgress
                        sx={{
                          color: "var(--orange-500)",
                        }}
                        size={30}
                      />
                    </Stack>
                  )}
                </Grid>
              ) : (
                <NotificationsEmptyState />
              )}
            </Grid>
          </PullToRefresh>
        </Box>
      </PermissionWrapper>
    );
  }, [inAppNotification, isMobile, isTablet, intl, closeHandler]);

  useEffect(() => {
    if (unreadNotificationIds?.length && !isAllNotificationRead) {
      changeNotificationStatus();
    }
  }, [unreadNotificationIds, isAllNotificationRead]);

  return (
    <>
      {isMobile || isTablet ? (
        <FullScreenDialog>{notificationInnerContent}</FullScreenDialog>
      ) : (
        <Dialog
          isOpen={isOpen}
          closeHandler={notificationCloseHandler}
          paperProps={{
            sx: {
              position: "absolute",
              top: 80, // Adjust distance from top
              right: 125, // Adjust distance from right
              margin: 0,
              borderRadius: "1.6rem",
              height: "85vh",
              width: "46.4rem",
            },
          }}
        >
          {notificationInnerContent}
        </Dialog>
      )}
    </>
  );
};

export default Notification;
